import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BarChart3, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { formatDate, formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { feedbackStatusLabel, useFeedback, useFeedbackMutations } from '../api';
import { useAdminSurveys, useSurveyMutations } from './api';
import { EditSurveyPanel } from './EditSurveyPanel';
import { SurveyPreviewById } from './SurveyPreview';
import { SurveyBuilder } from './SurveyBuilder';
import { emptyDraft, type SurveyDraft } from './draft';
import { SurveyResults } from './SurveyResults';

/** סקרים ותיבת משוב, בצד הניהול (PRD 5.10.5). */
export function SurveysAdminPage() {
  const confirm = useConfirm();
  const canManage = usePerm('surveys.manage');
  const canReadFeedback = usePerm('feedback.read');
  const surveys = useAdminSurveys();
  const mutations = useSurveyMutations();
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState<SurveyDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [openResults, setOpenResults] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage && !canReadFeedback) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <AdminPage
      title="סקרים ומשוב"
      subtitle="מה העובדים חושבים"
      actions={
        canManage &&
        !isAdding && (
          <Button variant="primary" onClick={() => setIsAdding(true)}>
            <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
            סקר חדש
          </Button>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {isAdding && (
        <Panel title="סקר חדש" className="overflow-visible">
          <SurveyBuilder
            draft={draft}
            onChange={setDraft}
            saving={mutations.save.isPending}
            onCancel={() => setIsAdding(false)}
            onSubmit={() =>
              run(async () => {
                await mutations.save.mutateAsync({
                  title: draft.title,
                  description: draft.description.trim() || null,
                  analysis_note: draft.analysis_note.trim() || null,
                  is_anonymous: draft.is_anonymous,
                  starts_at: draft.starts_at ? new Date(draft.starts_at).toISOString() : null,
                  ends_at: draft.ends_at ? new Date(draft.ends_at).toISOString() : null,
                  questions: draft.questions,
                });
                setDraft(emptyDraft());
                setIsAdding(false);
              })
            }
          />
        </Panel>
      )}

      {editingId && (
        <EditSurveyPanel
          surveyId={editingId}
          onDone={() => setEditingId(null)}
          onError={setError}
        />
      )}

      {canManage && (
        <>
          {surveys.isPending && <Skeleton className="h-32" />}
          {surveys.isError && <ErrorState onRetry={() => void surveys.refetch()} />}

          {surveys.data?.length === 0 && !isAdding && (
            <EmptyState title="אין סקרים" description="סקר קצר הוא הדרך לשמוע מה באמת קורה." />
          )}

          {surveys.data && surveys.data.length > 0 && (
            <Table>
              <thead>
                <Tr>
                  <Th>שם</Th>
                  <Th>סוג</Th>
                  <Th>נפתח</Th>
                  <Th>נסגר</Th>
                  <Th>
                    <span className="sr-only">פעולות</span>
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {surveys.data.map((survey) => (
                  <Tr key={survey.id}>
                    <Td>{survey.title}</Td>
                    <Td>
                      {survey.is_anonymous ? (
                        <StatusBadge tone="warn" icon={EyeOff}>
                          אנונימי
                        </StatusBadge>
                      ) : (
                        <span className="text-muted">עם שם</span>
                      )}
                    </Td>
                    <Td className="tabular">{formatDate(survey.starts_at)}</Td>
                    <Td className="tabular">
                      {survey.ends_at ? formatDate(survey.ends_at) : 'פתוח'}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          aria-label={`תצוגה מקדימה של ${survey.title}`}
                          onClick={() => setPreviewId(survey.id)}
                        >
                          <Eye size={20} strokeWidth={1.75} aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          aria-label={`עריכת ${survey.title}`}
                          onClick={() => {
                            setIsAdding(false);
                            setEditingId(survey.id);
                          }}
                        >
                          <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          aria-label="תוצאות"
                          onClick={() =>
                            setOpenResults(openResults === survey.id ? null : survey.id)
                          }
                        >
                          <BarChart3 size={20} strokeWidth={1.75} aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          aria-label={`מחיקת ${survey.title}`}
                          onClick={() =>
                            confirm.ask({
                              title: 'מחיקת סקר',
                              body: (
                                <>
                                  <p className="m-0">"{survey.title}"</p>
                                  <p className="m-0 text-body-sm text-muted">
                                    התשובות שעובדים כבר הגישו יימחקו איתו, ואי אפשר לשחזר אותן.
                                  </p>
                                </>
                              ),
                              confirmLabel: 'מחיקת הסקר והתשובות',
                              onConfirm: () => run(() => mutations.remove.mutateAsync(survey.id)),
                            })
                          }
                        >
                          <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}

          {openResults && <SurveyResults surveyId={openResults} />}
        </>
      )}

      {canReadFeedback && <FeedbackInbox />}

      {previewId && <SurveyPreviewById surveyId={previewId} onClose={() => setPreviewId(null)} />}
    </AdminPage>
  );
}

function FeedbackInbox() {
  const feedback = useFeedback();
  const { setStatus } = useFeedbackMutations();

  const items = feedback.data ?? [];

  return (
    <Panel title={`תיבת משוב (${items.filter((item) => item.status === 'new').length} חדשים)`}>
      {feedback.isPending && <Skeleton className="h-24" />}
      {items.length === 0 && !feedback.isPending && (
        <p className="m-0 text-body-sm text-muted">אין משוב בתיבה.</p>
      )}

      <div className="grid gap-2">
        {items.map((item) => (
          <div key={item.id} className="grid gap-2 rounded-md border border-line p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-label text-muted">
                {item.feedback_categories?.name ?? 'בלי נושא'}
              </span>
              <span className="text-label text-muted">{formatDateTime(item.created_at)}</span>
              <span className="grow" />
              <StatusBadge tone={item.status === 'handled' ? 'good' : 'warn'}>
                {feedbackStatusLabel[item.status]}
              </StatusBadge>
            </div>

            <p className="m-0 whitespace-pre-line text-body">{item.body}</p>

            <p className="m-0 text-label text-muted">
              {item.employees
                ? `${item.employees.first_name} ${item.employees.last_name}${
                    item.branches ? ` · ${item.branches.name}` : ''
                  }`
                : 'נשלח בלי שם'}
            </p>

            {item.status !== 'handled' && (
              <div className="flex flex-wrap gap-2">
                {item.status === 'new' && (
                  <Button
                    onClick={() => void setStatus.mutateAsync({ id: item.id, status: 'read' })}
                  >
                    סימון כנקרא
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => void setStatus.mutateAsync({ id: item.id, status: 'handled' })}
                >
                  טופל
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
